'use client';

import React from 'react';
import { Button } from '@/components/Buttons/Button';
import { Icon } from '@/components/Icons/Icon';
import { RECONNECT_MAX_RETRIES } from '../constants';
import type { AvatarState, ConnectionState } from '../types';

export interface ConnectionOverlayProps {
  connectionState: ConnectionState;
  avatarState: AvatarState;
  retryCount: number;
  /** Whether the browser currently reports no connectivity. */
  isOffline: boolean;
  errorMessage: string;
  isSmUp: boolean;
  /** Start the conversation (initial connect). */
  onStart: () => void;
  /** Cancel an in-progress reconnect. */
  onCancel: () => void;
  /** Retry after an error. */
  onRetry: () => void;
  /** Exit the simulation. */
  onExit: () => void;
}

/** Only the parked-offline and error states dim the stage; loading sits on it undimmed. */
const Scrim: React.FC = () => (
  <div
    data-testid="connection-overlay-scrim"
    className="absolute inset-0 bg-neutral-gray-800 opacity-85 backdrop-blur-sm"
  />
);

/**
 * Centered overlay shown over the avatar stage while the session is
 * connecting/reconnecting, after an error, or before the user has started.
 * The caller is responsible for gating visibility (it is hidden once the
 * avatar is active or an audio-only visualization is shown).
 *
 * While offline the overlay dims the avatar and says nothing: the session is
 * parked rather than working, and OfflineBanner already carries the message.
 */
export const ConnectionOverlay: React.FC<ConnectionOverlayProps> = ({
  connectionState,
  avatarState,
  retryCount,
  isOffline,
  errorMessage,
  isSmUp,
  onStart,
  onCancel,
  onRetry,
  onExit,
}) => {
  const isConnecting =
    connectionState === 'connecting' ||
    connectionState === 'reconnecting' ||
    connectionState === 'connected' ||
    connectionState === 'ready' ||
    avatarState === 'connecting';

  const isParkedOffline = connectionState === 'reconnecting' && isOffline;

  const statusText =
    connectionState === 'connecting'
      ? 'Connecting to session...'
      : connectionState === 'reconnecting'
        ? `Reconnecting... (${retryCount}/${RECONNECT_MAX_RETRIES})`
        : connectionState === 'connected'
          ? 'Initializing...'
          : avatarState === 'connecting'
            ? 'Starting avatar...'
            : 'Preparing avatar...';

  return (
    <div className="absolute inset-0 flex items-center justify-center">
      {isConnecting ? (
        isParkedOffline ? (
          <Scrim />
        ) : (
          <div className="relative flex flex-col items-center gap-4">
            <Icon
              name="CircleNotch"
              size={48}
              className="animate-spin-clockwise text-brand-primary animate-duration-1000 animate-iteration-count-infinite"
            />
            <p className="typography-body text-neutral-gray-800">{statusText}</p>
            {connectionState === 'reconnecting' && (
              <Button variant="tertiary" onClick={onCancel} className="mt-2">
                End session
              </Button>
            )}
          </div>
        )
      ) : errorMessage ? (
        <>
          <Scrim />
          <div className="relative flex max-w-md flex-col items-center gap-4 px-4">
            {/* Light-red variants: the on-light status/brand reds sit at ~1:1 against the scrim. */}
            <Icon name="Warning" size={48} className="text-brand-primary-100" />
            <p className="typography-body text-center text-neutral-white">{errorMessage}</p>
            <div className="flex gap-3">
              <Button variant="primary" onClick={onRetry} className="h-14 px-8 py-5">
                Retry
              </Button>
              <Button
                variant="tertiary"
                onClick={onExit}
                className="h-14 px-8 py-5 text-neutral-white hover:text-neutral-gray-300 focus-visible:text-brand-primary-700 active:text-brand-primary-700"
              >
                Exit
              </Button>
            </div>
          </div>
        </>
      ) : (
        <Button variant="secondary" onClick={onStart} size={isSmUp ? 'medium' : 'small'}>
          <span className="sm:hidden">Start</span>
          <span className="hidden sm:inline">🎭 Start Conversation</span>
        </Button>
      )}
    </div>
  );
};
